import useDeleteOperation from 'api/operation/common/useDeleteOperation';
import useInsertTransferOperation from 'api/operation/transfer/useInsertTransferOperation';
import useTransferOperationList from 'api/operation/transfer/useTransferOperationList';
import useUpdateTransferOperation from 'api/operation/transfer/useUpdateTransferOperation';
import OperationEditModal from 'components/private/Modals/operation/OperationEditModal';
import TransferOperationInsertModal from 'components/private/Modals/operation/transfer/TransferOperationInsertModal/TransferOperationInsertModal';
import DeleteModal from 'components/shared/DeleteModal/DeleteModal';
import PageSpinner from 'components/shared/pageSpinner/PageSpinner';
import RequireOperationSettings from 'components/shared/RequiredOpertaionSettings/RequireOperationSettings';
import ApiPaginateTable from 'components/shared/Table/ApiPaginateTable';
import useTransferOperationTableColumns from 'hooks/operation/useTransferOperationTableColumns';
import { permissions } from 'hooks/permissions/permissions';
import { usePermissions } from 'hooks/permissions/usePermissions';
import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { useCookies } from 'react-cookie';
import { useNavigate } from 'react-router-dom';

const TransferOperationList = () => {
  const { can } = usePermissions();
  const [{ operationSettings }] = useCookies(['operationSettings']);
  const zoneGuid = useMemo(() => {
    return operationSettings?.defaultZone?.key;
  }, [operationSettings?.defaultZone?.key]);

  const transferPermissions =
    permissions.general.categories.warehouseOperations.transfer;

  const canCreate = can(transferPermissions.create);
  const canDelete = can(transferPermissions.delete);
  const canEdit = can(transferPermissions.edit);

  const navigate = useNavigate();
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState('100');
  const [selectedRows, setSelectedRows] = useState([]);
  const { getTransferOperationList, loading, transferOperationList, pageRef } =
    useTransferOperationList();

  const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);
  const [isEditModalOpen, setIsEditModalOpen] = useState(false);
  const [isInsertModalOpen, setIsInsertModalOpen] = useState(false);
  const [deleteChosenTransferOperation, setDeleteChosenTransferOperation] =
    useState({});
  const [deleteLoading, setDeleteLoading] = useState(false);
  const [editLoading, setEditLoading] = useState(false);
  const [insertLoading, setInsertLoading] = useState(false);
  const [transferOperationEditValues, setTransferOperationEditValues] =
    useState({});

  const { deleteRequest } = useDeleteOperation();
  const { updateRequest } = useUpdateTransferOperation();
  const { insertRequest } = useInsertTransferOperation();

  const tableRef = useRef(null);

  const fetchList = useCallback(
    ({ page: pageNumber = 1, count = pageSize, term = '' } = {}) => {
      if (!zoneGuid) return;
      getTransferOperationList({
        Guid: zoneGuid,
        Page: pageNumber,
        Count: count,
        Term: term,
      });
    },
    [getTransferOperationList, pageSize, zoneGuid],
  );

  useEffect(() => {
    fetchList({ page: 1, count: pageSize });
  }, []);

  useEffect(() => {
    fetchList({ page, count: pageSize });
  }, [pageSize]);

  const deleteItem = useCallback(
    row => () => {
      setDeleteChosenTransferOperation(row);
      setIsDeleteModalOpen(true);
    },
    [],
  );

  const editItem = useCallback(
    row => () => {
      setTransferOperationEditValues(row);
      setIsEditModalOpen(true);
    },
    [],
  );

  const openItem = useCallback(
    row => () => {
      navigate(`/operation/transfer-workspace/${row.guid}`);
    },
    [navigate],
  );
  
  const [columnsData] = useTransferOperationTableColumns(
    deleteItem,
    editItem,
    tableRef,
    {
      canEdit,
      canDelete,
    },
  );

  const deleteHandle = guid => {
    setDeleteLoading(true);
    deleteRequest(
      guid,
      () => fetchList({ page, count: pageSize }),
      setIsDeleteModalOpen,
      setDeleteLoading,
    );
  };

  const editHandle = data => {
    if (!data) return;

    setEditLoading(true);
    updateRequest(
      {
        guid: transferOperationEditValues.guid,
        title: data.title,
      },
      () => fetchList({ page, count: pageSize }),
      setIsEditModalOpen,
      setEditLoading,
    );
  };

  const insertHandle = payload => {
    setInsertLoading(true);
    insertRequest(
      () => fetchList({ page, count: pageSize }),
      setIsInsertModalOpen,
      setInsertLoading,
      payload,
    );
  };

  const closeDeleteModal = () => setIsDeleteModalOpen(false);
  const closeEditModal = () => setIsEditModalOpen(false);

  function getPageData(pageNumber) {
    setPage(pageNumber);
    fetchList({ page: pageNumber, count: pageSize });
  }

  function handleSearch(text) {
    setPage(1);
    fetchList({ page: 1, count: pageSize, term: text });
  }

  return (
    <RequireOperationSettings>
      <div>{pageRef.refTitle}</div>

      {loading ? (
        <PageSpinner loading />
      ) : (
        <ApiPaginateTable
          columns={columnsData}
          rowsData={transferOperationList?.[page] ?? []}
          rows={transferOperationList?.[page] ?? []}
          pageSize={pageSize}
          isDeletable={canDelete}
          isLoading={loading}
          addLable={canCreate ? 'عملیات انتقال جدید' : undefined}
          onAddClick={canCreate ? () => setIsInsertModalOpen(true) : undefined}
          setPageSize={setPageSize}
          apiRef={tableRef}
          onPageChange={getPageData}
          page={page}
          countPages={
            pageRef?.totalCount
              ? Math.ceil(pageRef.totalCount / Number(pageSize))
              : 1
          }
          handleSearchApi={handleSearch}
          setSelectedRows={setSelectedRows}
          selectedRows={selectedRows}
        />
      )}

      {canDelete ? (
        <DeleteModal
          open={isDeleteModalOpen}
          handleClose={closeDeleteModal}
          locationId={deleteChosenTransferOperation.guid}
          title={deleteChosenTransferOperation.title}
          deleteLoading={deleteLoading}
          onDelete={deleteHandle}
        />
      ) : null}

      {canEdit ? (
        <OperationEditModal
          open={isEditModalOpen}
          handleClose={closeEditModal}
          editLoading={editLoading}
          onEdit={editHandle}
          initialValues={transferOperationEditValues}
        />
      ) : null}

      {canCreate ? (
        <TransferOperationInsertModal
          open={isInsertModalOpen}
          handleClose={() => setIsInsertModalOpen(false)}
          insertLoading={insertLoading}
          onInsert={insertHandle}
        />
      ) : null}
    </RequireOperationSettings>
  );
};

export default TransferOperationList;
